import axios, { AxiosHeaders, type AxiosRequestConfig } from "axios";
import { apiBaseUrl, authCsrfCookieName } from "../config/env";
import { AdminApiError, extractErrorMessage, emitSessionExpired } from "./errors";

function isLoopbackHost(hostname: string): boolean {
  const lowered = hostname.toLowerCase();
  return lowered === "localhost" || lowered === "127.0.0.1" || lowered === "::1" || lowered === "[::1]";
}

function resolveBrowserApiBase(baseUrl: string): string {
  if (!baseUrl || typeof window === "undefined") {
    return baseUrl;
  }

  try {
    const parsed = new URL(baseUrl);
    const currentHost = window.location.hostname;
    if (!currentHost) {
      return baseUrl;
    }

    if (!isLoopbackHost(parsed.hostname) || !isLoopbackHost(currentHost)) {
      return baseUrl;
    }

    if (parsed.hostname === currentHost) {
      return baseUrl;
    }

    parsed.hostname = currentHost;
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return baseUrl;
  }
}

const resolvedApiBaseUrl = resolveBrowserApiBase(apiBaseUrl);

const instance = axios.create({
  baseURL: `${resolvedApiBaseUrl}/api`,
  withCredentials: true,
  xsrfCookieName: authCsrfCookieName,
  xsrfHeaderName: "X-CSRF-Token",
});

const CSRF_COOKIE_NAMES = [
  authCsrfCookieName,
  "ztn_prod_csrf",
  "ztn_stg_csrf",
  "csrf-token",
].filter((v, i, a): v is string => Boolean(v) && a.indexOf(v) === i);
const CSRF_HEADER_NAME = "X-CSRF-Token";
const RETRY_FLAG = "__csrfRetried";

function isMutatingMethod(method?: string): boolean {
  const normalized = method?.toUpperCase();
  return normalized === "POST" || normalized === "PUT" || normalized === "PATCH" || normalized === "DELETE";
}

function readCookieValue(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const pattern = new RegExp(`(?:^|; )${name.replace(/([.*+?^${}()|[\]\\])/g, "\\$&")}=([^;]*)`);
  const match = document.cookie.match(pattern);
  if (!match) return undefined;
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

function readCsrfToken(): string | undefined {
  for (const name of CSRF_COOKIE_NAMES) {
    const value = readCookieValue(name);
    if (value) return value;
  }
  return undefined;
}

function writeCsrfToken(token: string) {
  if (typeof document === "undefined") {
    return;
  }

  const secureFlag = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CSRF_COOKIE_NAMES[0]}=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secureFlag}`;
}

instance.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (method && method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    const csrfToken = readCsrfToken();
    if (csrfToken) {
      let headers: AxiosHeaders;
      if (config.headers instanceof AxiosHeaders) {
        headers = config.headers;
      } else {
        headers = AxiosHeaders.from(config.headers ?? {});
        config.headers = headers;
      }

      headers.set(CSRF_HEADER_NAME, csrfToken);
    }

    if (!(config.data instanceof FormData)) {
      let headers: AxiosHeaders;
      if (config.headers instanceof AxiosHeaders) {
        headers = config.headers;
      } else {
        headers = AxiosHeaders.from(config.headers ?? {});
        config.headers = headers;
      }

      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
    }
  }

  return config;
});

instance.interceptors.response.use(
  (response) => {
    const headerToken =
      response.headers?.[CSRF_HEADER_NAME] ??
      response.headers?.[CSRF_HEADER_NAME.toLowerCase()];
    if (headerToken) {
      writeCsrfToken(headerToken as string);
    }
    return response;
  },
  async (error) => {
    const headerToken =
      error?.response?.headers?.[CSRF_HEADER_NAME] ??
      error?.response?.headers?.[CSRF_HEADER_NAME.toLowerCase()];
    if (headerToken) {
      writeCsrfToken(headerToken as string);
    }

    const status: number = error?.response?.status ?? 0;
    const originalConfig = error?.config as (AxiosRequestConfig & Record<string, unknown>) | undefined;
    const requestMethod = typeof originalConfig?.method === "string" ? originalConfig.method : undefined;
    const alreadyRetried = Boolean(originalConfig?.[RETRY_FLAG]);

    if (status === 403 && isMutatingMethod(requestMethod) && !alreadyRetried) {
      try {
        await instance.get("/auth/csrf/refresh");
        const retryConfig: AxiosRequestConfig & Record<string, unknown> = {
          ...(originalConfig ?? {}),
        };
        retryConfig[RETRY_FLAG] = true;
        return await instance.request(retryConfig);
      } catch {
        // Refresh failed; return original wrapped error below.
      }
    }

    const data = error?.response?.data;
    const message = extractErrorMessage(data, error?.message ?? "Bilinmeyen hata");
    const wrapped = new AdminApiError(status, message, data);

    if (status === 401) {
      emitSessionExpired(status);
    }

    return Promise.reject(wrapped);
  },
);

export default instance;
